import { WorkOrderForm } from '@/components/features/WorkOrders/forms/WorkOrderForm';
import { useTranslations } from '@/hooks/useTranslations';
import CreateLayout from '@/layouts/create-layout';
import { type Company, type User } from '@/types';
import { router, useForm, usePage } from '@inertiajs/react';
import { useMemo } from 'react';

interface SettingOption {
    value: string;
    label: string;
    color?: string;
    icon?: string;
}

interface WorkOrderCreateProps {
    users: User[];
    companies: Company[];
    statuses: SettingOption[];
    priorities: SettingOption[];
    types: SettingOption[];
}

export default function WorkOrderCreate({ users, companies, statuses = [], priorities = [], types = [] }: WorkOrderCreateProps) {
    const page = usePage();
    const { t } = useTranslations();
    // Inertia passes errors in props.errors
    const errors = (page.props as { errors?: Record<string, string> }).errors || {};
    const translatedStatuses = useMemo(() => {
        return statuses.map((status) => ({
            ...status,
            label: t(`settings.work_order_statuses.${status.value}`, status.label),
        }));
    }, [statuses, t]);

    const translatedPriorities = useMemo(() => {
        return priorities.map((priority) => ({
            ...priority,
            label: t(`settings.work_order_priorities.${priority.value}`, priority.label),
        }));
    }, [priorities, t]);

    const translatedTypes = useMemo(() => {
        return types.map((type) => {
            const translationKeyValue = type.value.trim().toLowerCase().replace(/\s+/g, '_');
            return {
                ...type,
                label: t(`settings.work_order_types.${translationKeyValue}`, type.label),
            };
        });
    }, [types, t]);

    const { setData } = useForm({
        contact_first_name: '',
        contact_last_name: '',
        phone: '',
        email: '',
        service_address: '',
        description: '',
        status: translatedStatuses[0]?.value || '',
        priority: translatedPriorities[0]?.value || '',
        work_type: translatedTypes[0]?.value || '',
        company_id: '',
        assigned_user_id: '',
        paying_company_id: '',
        estimated_date: '',
        assignments: [] as Array<{ user_id: string; role: 'lead' | 'helper' }>,
        service_address_lat: null as number | null,
        service_address_lng: null as number | null,
        photos: [] as File[],
        assigned_user_roles: '',
        service_address_street: '',
        service_address_house_number: '',
        service_address_postal_code: '',
        service_address_city: '',
        service_address_municipality: '',
    });

    return (
        <CreateLayout
            breadcrumbs={[
                { title: t('work_orders.create.breadcrumbs.dashboard', 'Dashboard'), href: '/' },
                { title: t('work_orders.create.breadcrumbs.work_orders', 'Work Orders'), href: '/work-orders' },
                { title: t('work_orders.create.breadcrumbs.create', 'Create'), href: '#' },
            ]}
            title={t('work_orders.create.heading', 'Create Work Order')}
            subtitle={t('work_orders.create.subheading', 'Create and manage work orders efficiently')}
        >
            <WorkOrderForm
                users={users}
                companies={companies}
                statuses={translatedStatuses}
                priorities={translatedPriorities}
                types={translatedTypes}
                errors={errors}
                onSubmit={(formData) => {
                    // Update the local form helper just in case we need it later,
                    // though we use router directly here.
                    Object.keys(formData).forEach((key) => {
                        // @ts-expect-error - key indexing on form data
                        setData(key, formData[key as keyof typeof formData]);
                    });

                    router.post(
                        route('work-orders.store'),
                        {
                            ...formData,
                            work_type: formData.type,
                        },
                        {
                            forceFormData: true,
                        },
                    );
                }}
            />
        </CreateLayout>
    );
}
